import { z } from "zod";

import { OpaqueIdSchema, UsernameSchema } from "./identity-schemas";
import { WorkspaceSubdomainSchema } from "./schemas";

/**
 * Who owns a field the internal organization view renders.
 *
 * The distinction is a product requirement, not decoration: an employee reading this view has to
 * know whether a wrong value is fixed by the customer's own administrator, by the customer's
 * identity provider, or by Delta Adaptive Systems operations. Rendering all three as one
 * undifferentiated table has previously led employees to "correct" customer-owned data.
 */
export const OrganizationDataOwnershipSchema = z.enum(["customer", "directory", "internal"]);
export type OrganizationDataOwnership = z.infer<typeof OrganizationDataOwnershipSchema>;

export const OrganizationContractSchema = z.strictObject({
  status: z.enum(["trial", "active", "suspended", "terminated"]),
  startsAt: z.number().int().positive(),
  expiresAt: z.number().int().positive().nullable(),
  toolLimit: z.number().int().nonnegative().nullable(),
  userLimit: z.number().int().nonnegative().nullable(),
});
export type OrganizationContract = z.infer<typeof OrganizationContractSchema>;

/**
 * Which sign-in methods the workspace offers (ADR-0061) and whether it is pinned to its own
 * address (ADR-0063). Read-only here; the customer owns the flags and operations owns the address.
 */
export const OrganizationSignInPolicySchema = z.strictObject({
  emailPassword: z.boolean(),
  googleSso: z.boolean(),
  samlSso: z.boolean(),
  requireWorkspaceHost: z.boolean(),
});

export const OrganizationWorkspaceHostSchema = z.strictObject({
  subdomain: WorkspaceSubdomainSchema,
  isPrimary: z.boolean(),
});

export const OrganizationAccountSchema = z.strictObject({
  status: z.enum(["active", "suspended", "closed"]),
  provisioningStatus: z.enum([
    "requested",
    "provisioning",
    "verifying",
    "ready",
    "failed",
    "degraded",
    "retiring",
  ]),
  createdAt: z.number().int().positive(),
  contract: OrganizationContractSchema.nullable(),
  signInPolicy: OrganizationSignInPolicySchema,
  workspaceHosts: z.array(OrganizationWorkspaceHostSchema).max(20),
  samlEnabled: z.boolean(),
  activeUserCount: z.number().int().nonnegative(),
  activeToolCount: z.number().int().nonnegative(),
});

/**
 * How this person proves who they are *inside this organization*.
 *
 * `saml` is deliberately org-scoped: a SAML identity belongs to one connection, and a connection
 * belongs to one organization, so a person federated at customer A must not read as directory-
 * managed while an employee is looking at customer B. `google` identities are global, and every
 * account also carries a password hash, so `password` means "no directory identity applies here".
 */
export const OrganizationIdentitySourceSchema = z.enum(["password", "google", "saml"]);
export type OrganizationIdentitySource = z.infer<typeof OrganizationIdentitySourceSchema>;

export const OrganizationMemberSchema = z.strictObject({
  userId: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  username: UsernameSchema,
  email: z.email().max(320).nullable(),
  role: z.enum(["platform_admin", "organization_admin", "member"]),
  membershipStatus: z.enum(["active", "disabled"]),
  accountStatus: z.enum(["active", "disabled"]),
  identitySources: z.array(OrganizationIdentitySourceSchema).max(3),
  suspendedAt: z.number().int().positive().nullable(),
  toolAccessCount: z.number().int().nonnegative(),
});
export type OrganizationMember = z.infer<typeof OrganizationMemberSchema>;

/**
 * The active deployment behind one tool. Deliberately excludes the Worker name, generated and
 * frozen config hashes, bundle hash, migration and binding snapshots, and every physical resource
 * identifier: an employee supporting a customer needs the version and when it shipped, not the
 * means to address the private Worker.
 */
export const OrganizationDeploymentSchema = z.strictObject({
  versionId: z.string().length(36).nullable(),
  templateVersion: z.string().min(1).max(64),
  status: z.enum(["building", "deployed", "failed", "superseded"]),
  deployedAt: z.number().int().positive().nullable(),
  failureStage: z.string().min(1).max(120).nullable(),
});

export const OrganizationToolSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  status: z.enum(["provisioning", "active", "disabled", "error"]),
  activeDeployment: OrganizationDeploymentSchema.nullable(),
  accessCount: z.number().int().nonnegative(),
});
export type OrganizationTool = z.infer<typeof OrganizationToolSchema>;

export const OrganizationSupportEventSchema = z.strictObject({
  id: OpaqueIdSchema,
  employeeDisplayName: z.string().min(1).max(120),
  targetUserDisplayName: z.string().min(1).max(120).nullable(),
  mode: z.enum(["read", "write"]),
  reason: z.string().min(10).max(500),
  createdAt: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
  revokedAt: z.number().int().positive().nullable(),
  state: z.enum(["active", "expired", "revoked"]),
});
export type OrganizationSupportEvent = z.infer<typeof OrganizationSupportEventSchema>;

export const ORGANIZATION_MEMBER_LIMIT = 200;
export const ORGANIZATION_TOOL_LIMIT = 200;
export const ORGANIZATION_SUPPORT_HISTORY_LIMIT = 50;

export const OrganizationPendingInvitationSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(2).max(120),
  email: z.email().max(320).nullable(),
  role: z.enum(["organization_admin", "member"]),
  provider: z.enum(["google", "saml"]).nullable(),
  expiresAt: z.number().int().positive(),
  state: z.enum(["pending", "expired"]),
});

export const OrganizationOverviewSchema = z.strictObject({
  account: OrganizationAccountSchema,
  members: z.array(OrganizationMemberSchema).max(ORGANIZATION_MEMBER_LIMIT),
  memberTotal: z.number().int().nonnegative(),
  pendingInvitations: z.array(OrganizationPendingInvitationSchema).max(ORGANIZATION_MEMBER_LIMIT),
  pendingInvitationTotal: z.number().int().nonnegative(),
  tools: z.array(OrganizationToolSchema).max(ORGANIZATION_TOOL_LIMIT),
  toolTotal: z.number().int().nonnegative(),
  /**
   * `null` means "this employee may not read support history", which is a different fact from an
   * empty array and has to survive the wire so the browser can say which one it is.
   */
  supportHistory: z
    .array(OrganizationSupportEventSchema)
    .max(ORGANIZATION_SUPPORT_HISTORY_LIMIT)
    .nullable(),
  supportHistoryTotal: z.number().int().nonnegative().nullable(),
});
export type OrganizationOverview = z.infer<typeof OrganizationOverviewSchema>;

export const OrganizationOverviewResponseSchema = z.strictObject({
  organization: OrganizationOverviewSchema,
});

export const OPS_PEOPLE_SEARCH_LIMIT = 25;
export const OPS_PEOPLE_ORGANIZATION_LIMIT = 20;

export const OpsPeopleQuerySchema = z.strictObject({
  q: z.string().trim().min(2).max(320),
});

export const OpsPersonOrganizationSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/),
  name: z.string().min(2).max(160),
  role: z.enum(["platform_admin", "organization_admin", "member"]),
  membershipStatus: z.enum(["active", "disabled"]),
  organizationStatus: z.enum(["active", "suspended", "closed"]),
});

export const OpsPersonSchema = z.strictObject({
  userId: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  username: UsernameSchema,
  email: z.email().max(320).nullable(),
  accountStatus: z.enum(["active", "disabled"]),
  organizations: z.array(OpsPersonOrganizationSchema).max(OPS_PEOPLE_ORGANIZATION_LIMIT),
});
export type OpsPerson = z.infer<typeof OpsPersonSchema>;

export const OpsPeopleResponseSchema = z.strictObject({
  people: z.array(OpsPersonSchema).max(OPS_PEOPLE_SEARCH_LIMIT),
  /**
   * True when the search stopped at the limit, so the browser can say the list is bounded rather
   * than implying the customer has exactly this many matching people.
   */
  truncated: z.boolean(),
});
